// Real auth. AuthProvider probes /api/v1/auth/me on mount; user is null until
// that resolves. signIn POSTs credentials; the server sets an HttpOnly cookie
// (rev9_auth) so the SPA never touches the token directly. RequireAuth gates
// the portal subtree.

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { api } from './api';

export type AuthRole = 'manager' | 'client' | 'auditor';

export interface ImpersonationMarker {
  id: string;
  email: string;
  display_name: string;
  started_at: string;
}

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  client_legal_name: string;
  role: AuthRole;
  tenant_id: string;
  is_superuser: boolean;
  // Whether the user has a confirmed TOTP device (from /auth/me). 17.E forces
  // managers to enroll before using the app.
  two_factor_enabled: boolean;
  // OB.2 — true for a portal-invited investor who holds fund units but has no SMA
  // accounts; they land on `/fund` (their real position), not the $0 SMA dashboard.
  is_fund_only_investor?: boolean;
  // Non-null when this session is a super-user "view as" overlay (slice 13.a):
  // the acting super-user. Drives the impersonation banner.
  impersonated_by?: ImpersonationMarker | null;
}

export interface SignInResult {
  user: AuthUser | null;
  twoFactorRequired?: boolean;
  challenge?: string;
}

// The treasury wallet-balances viewer is an N8-internal page (it shows N8's
// own treasury addresses + balances), not a per-tenant feature. Because any
// self-service sign-up becomes a manager of a new tenant, access is gated on
// the account email domain — matching the backend IsN8Capital permission.
// Exact-domain, case-insensitive suffix (a look-alike like `@n8.capital.evil`
// does NOT pass).
export const N8_STAFF_EMAIL_DOMAIN = 'n8.capital';

export function isN8User(user: AuthUser | null | undefined): boolean {
  if (!user) return false;
  return user.email.trim().toLowerCase().endsWith(`@${N8_STAFF_EMAIL_DOMAIN}`);
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  verify2fa: (challenge: string, code: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  // Re-probe /auth/me and replace the cached user. After starting or stopping a
  // "view as" overlay the SAME cookie resolves to a different identity, so the
  // SPA must re-fetch to re-render as the target (or back to the super-user).
  refreshUser: () => Promise<AuthUser | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api<AuthUser>('/auth/me')
      .then(me => { if (!cancelled) setUser(me); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    signIn: async (email, password) => {
      const resp = await api<{ user?: AuthUser; two_factor_required?: boolean; challenge?: string }>(
        '/auth/login', { method: 'POST', body: { email, password } },
      );
      if (resp.two_factor_required) {
        return { user: null, twoFactorRequired: true, challenge: resp.challenge };
      }
      setUser(resp.user ?? null);
      return { user: resp.user ?? null };
    },
    verify2fa: async (challenge, code) => {
      const resp = await api<{ user: AuthUser }>('/auth/login/2fa', {
        method: 'POST', body: { challenge, code },
      });
      setUser(resp.user);
      return resp.user;
    },
    signOut: async () => {
      try { await api('/auth/logout', { method: 'POST' }); }
      catch { /* server may already be logged out; clear local state regardless */ }
      setUser(null);
    },
    refreshUser: async () => {
      const me = await api<AuthUser>('/auth/me');
      setUser(me);
      return me;
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'grid', placeItems: 'center',
        background: 'var(--paper)', color: 'var(--ink-3)',
        fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase',
      }}>
        Verifying session…
      </div>
    );
  }
  if (!user) return <Navigate to="/sign-in" state={{ from: location }} replace />;
  return <>{children}</>;
}

export function RequireRole({ role, fallback = '/dashboard', children }: {
  role: AuthUser['role'] | AuthUser['role'][];
  fallback?: string;
  children: ReactNode;
}) {
  const { user } = useAuth();
  const allowed = Array.isArray(role) ? role : [role];
  if (!user || !allowed.includes(user.role)) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}

export function RequireSuperUser({ fallback = '/dashboard', children }: {
  fallback?: string;
  children: ReactNode;
}) {
  const { user } = useAuth();
  if (!user || !user.is_superuser) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}

// Manager OR super-user — mirrors the backend authz on the credential endpoints
// (`OpsAccountCredentialsView._resolve_account`: is_superuser or role=='manager').
// Clients are excluded (they manage their own keys via /settings, not this
// per-account admin surface).
export function RequireManagerOrSuperUser({ fallback = '/dashboard', children }: {
  fallback?: string;
  children: ReactNode;
}) {
  const { user } = useAuth();
  if (!user || !(user.is_superuser || user.role === 'manager')) {
    return <Navigate to={fallback} replace />;
  }
  return <>{children}</>;
}

// N8-internal treasury wallet-balances page — mirrors the backend IsN8Capital
// gate. Non-N8 users who reach the URL directly are bounced (the endpoint would
// 403 them anyway; this keeps them off the broken page). Silent redirect, no
// leak of why — the page simply isn't theirs.
export function RequireN8({ fallback = '/fund', children }: {
  fallback?: string;
  children: ReactNode;
}) {
  const { user } = useAuth();
  if (!isN8User(user)) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}

// 17.E — force managers to enroll 2FA before using the portal (a fund product
// handles investor capital). Clients/auditors keep 2FA optional. Wraps the portal
// subtree; the /security/2fa-setup route is OUTSIDE this gate so the manager can
// reach the enrollment page (else: redirect loop).
export function RequireManager2FA({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  // Super-user "view as" overlay (slice 13.a): the impersonated manager's 2FA
  // state isn't the super-user's to fix, and /auth/2fa/enroll is write-blocked
  // under the impersonation guard — gating here would trap the super-user on a
  // setup page they can never complete. Exempt impersonation sessions.
  if (user?.impersonated_by) return <>{children}</>;
  if (user && user.role === 'manager' && !user.two_factor_enabled) {
    return <Navigate to="/security/2fa-setup" replace />;
  }
  return <>{children}</>;
}


// ── Self-service registration (Phase 17.D) ──────────────────────────────────
// Anonymous helpers — no auth context needed (mirror PasswordReset calling api
// directly). The endpoints always return generic success bodies (anti-enum).

export async function register(
  firmName: string, email: string, password: string,
  firstName = '', lastName = '',
  extra: Record<string, string> = {},
) {
  return api('/auth/register', {
    method: 'POST',
    body: { firm_name: firmName, email, password, first_name: firstName, last_name: lastName, ...extra },
  });
}

export async function verifyEmail(uid: string, token: string) {
  return api('/auth/register/verify', { method: 'POST', body: { uid, token } });
}

export async function resendVerification(email: string) {
  return api('/auth/register/resend', { method: 'POST', body: { email } });
}
